import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtNum } from "@/utils/toEnglishDigits";
import {
  Folder,
  FolderOpen,
  FolderPlus,
  Plus,
  Pencil,
  Trash,
  Buildings,
  MagnifyingGlass,
  Tag as TagIcon,
  ArrowRight,
  Warning,
} from "@phosphor-icons/react";

/* --------------------------------------------------------------------------
 * قوالب أسعار المسارات (Route Pricing Templates)
 * كل قالب = مجلد يحتوي مجموعة قواعد تسعير + شركات مرتبطة به.
 * الشركة المرتبطة بقالب تستخدم أسعاره حصرياً (بدون fallback للأسعار العامة).
 * -------------------------------------------------------------------------- */

const emptyTemplate = () => ({ name: "", description: "", company_ids: [] });

function TemplateModal({ open, initial, allCompanies, onClose, onSaved }) {
  const [form, setForm] = useState(emptyTemplate());
  const [saving, setSaving] = useState(false);
  const [companyQ, setCompanyQ] = useState("");
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name || "",
              description: initial.description || "",
              company_ids: [...(initial.company_ids || [])],
            }
          : emptyTemplate(),
      );
      setCompanyQ("");
    }
  }, [open, initial]);

  if (!open) return null;

  const toggleCompany = (cid) => {
    setForm((f) => {
      const set = new Set(f.company_ids || []);
      if (set.has(cid)) set.delete(cid);
      else set.add(cid);
      return { ...f, company_ids: Array.from(set) };
    });
  };

  const q = companyQ.trim().toLowerCase();
  const filteredCompanies = !q
    ? allCompanies
    : allCompanies.filter(
        (c) =>
          (c.name || "").toLowerCase().includes(q) ||
          (c.email || "").toLowerCase().includes(q) ||
          (c.phone || "").includes(q),
      );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("اسم القالب مطلوب");
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/admin/pricing-templates/${initial.id}`, form);
        toast.success("تم تحديث القالب");
      } else {
        await api.post("/admin/pricing-templates", form);
        toast.success("تم إنشاء القالب");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mz-modal-backdrop" onClick={onClose} dir="rtl" data-testid="template-modal">
      <div className="mz-modal-panel" onClick={(e) => e.stopPropagation()}>
        <div className="mz-modal-header">
          <h3 className="text-primary font-bold text-lg flex items-center gap-2">
            <FolderPlus size={20} weight="duotone" />
            {isEdit ? "تعديل قالب أسعار" : "قالب أسعار جديد"}
          </h3>
          <button
            onClick={onClose}
            className="text-white/50 hover:text-white text-2xl leading-none"
            data-testid="template-modal-close"
          >
            ×
          </button>
        </div>
        <form onSubmit={submit} className="contents">
          <div className="mz-modal-body">
            <div className="space-y-4">
              <label className="block">
                <div className="text-xs text-white/60 mb-1 text-right">اسم القالب *</div>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mz-input"
                  data-testid="template-name"
                  placeholder="مثال: أسعار الشركات المميزة"
                  required
                />
              </label>

              <label className="block">
                <div className="text-xs text-white/60 mb-1 text-right">وصف القالب (اختياري)</div>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="mz-input min-h-[70px]"
                  data-testid="template-description"
                  placeholder="ملاحظات داخلية عن هذا القالب…"
                />
              </label>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-white/60 text-right">
                    الشركات المرتبطة (<span className="num">{fmtNum(form.company_ids.length)}</span>)
                  </div>
                  <div className="text-[11px] text-white/40">
                    اختر الشركات التي ستستخدم هذا القالب حصرياً
                  </div>
                </div>
                <div className="relative mb-2">
                  <MagnifyingGlass
                    size={14}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60"
                  />
                  <input
                    type="text"
                    value={companyQ}
                    onChange={(e) => setCompanyQ(e.target.value)}
                    placeholder="ابحث عن شركة…"
                    className="mz-input pr-9 text-sm"
                    data-testid="template-companies-search"
                  />
                </div>
                <div
                  className="max-h-64 overflow-y-auto rounded-xl border border-primary/20 bg-[#0B1A3A] p-2 space-y-1"
                  data-testid="template-companies-list"
                >
                  {filteredCompanies.length === 0 ? (
                    <div className="text-center text-white/40 text-xs py-6">
                      لا توجد شركات مطابقة
                    </div>
                  ) : (
                    filteredCompanies.map((c) => {
                      const checked = form.company_ids.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                            checked
                              ? "bg-primary/15 border border-primary/40"
                              : "border border-transparent hover:bg-white/5"
                          }`}
                          data-testid={`template-company-row-${c.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCompany(c.id)}
                            className="w-4 h-4 accent-primary"
                            data-testid={`template-company-check-${c.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-white text-sm truncate">{c.name}</div>
                            <div className="text-white/40 text-[11px] truncate" dir="ltr">
                              {c.email || c.phone || ""}
                            </div>
                          </div>
                          {c.pricing_template_id && c.pricing_template_id !== initial?.id && (
                            <span
                              className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F57F17]/20 border border-[#F57F17]/40 text-[#F57F17] font-bold"
                              title="مرتبطة حالياً بقالب آخر — سيتم نقلها"
                            >
                              مرتبطة بقالب آخر
                            </span>
                          )}
                        </label>
                      );
                    })
                  )}
                </div>
                <div className="mt-2 text-[11px] text-white/50 flex items-start gap-1.5">
                  <Warning size={12} className="text-[#F57F17] shrink-0 mt-0.5" weight="fill" />
                  <span>
                    الشركة المرتبطة بهذا القالب ستستخدم أسعاره حصرياً؛ ولن يتم الرجوع إلى الأسعار
                    العامة حتى لو لم يوجد سعر لمسار ما في القالب.
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="mz-modal-footer">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60"
              data-testid="template-save"
            >
              {saving ? "جارٍ الحفظ…" : isEdit ? "حفظ التعديلات" : "إنشاء القالب"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PricingTemplates() {
  const [templates, setTemplates] = useState([]);
  const [generalCount, setGeneralCount] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    // Fetch templates first — that's the primary data. Companies list is
    // secondary (only needed inside the modal); load it in parallel but
    // don't block the grid on it.
    let ok = false;
    try {
      const t = await api.get("/admin/pricing-templates");
      const raw = t.data || {};
      setTemplates(raw.templates || []);
      setGeneralCount(Number(raw.general_rules_count || 0));
      ok = true;
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
    // Kick off the companies fetch independently — it can be slow on large
    // DBs. If it fails, the modal will just show an empty list.
    if (ok) {
      api
        .get("/admin/companies?status=approved")
        .then((cmp) => {
          setCompanies(
            (cmp.data || []).map((c) => ({
              id: c.id,
              name: c.name,
              email: c.email,
              phone: c.phone,
              pricing_template_id: c.pricing_template_id || null,
            })),
          );
        })
        .catch(() => setCompanies([]));
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  // Merge live pricing_template_id from templates → companies (defensive
  // hydration in case /admin/companies didn't expose it yet).
  const enrichedCompanies = useMemo(() => {
    const linkMap = {};
    templates.forEach((t) => (t.company_ids || []).forEach((cid) => (linkMap[cid] = t.id)));
    return companies.map((c) => ({
      ...c,
      pricing_template_id: c.pricing_template_id || linkMap[c.id] || null,
    }));
  }, [companies, templates]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(query) ||
        (t.description || "").toLowerCase().includes(query),
    );
  }, [templates, q]);

  const remove = async (t) => {
    const rulesN = Number(t.rules_count || 0);
    const compN = (t.company_ids || []).length;
    const ok = await confirm({
      title: "حذف القالب",
      message: `سيتم حذف القالب "${t.name}"${
        rulesN > 0 ? ` مع ${rulesN} قاعدة سعر` : ""
      }${compN > 0 ? `، وسيتم فصل ${compN} شركة عنه (تعود للأسعار العامة).` : "."}\nهل أنت متأكد؟`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/pricing-templates/${t.id}`);
      toast.success("تم حذف القالب");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6" data-testid="pricing-templates-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
              <FolderOpen size={22} weight="duotone" className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                قوالب الأسعار (مجلدات)
              </h1>
              <p className="text-white/50 text-sm mt-0.5">
                <span className="num">{fmtNum(templates.length)}</span> قالب ·{" "}
                <span className="num">{fmtNum(generalCount)}</span> قاعدة سعر عامة
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            data-testid="add-template"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-xl font-bold"
          >
            <Plus size={18} weight="bold" /> قالب جديد
          </button>
        </div>

        {/* Informational banner */}
        <div className="mb-5 rounded-xl border border-primary/25 bg-primary/5 p-3 md:p-4 flex items-start gap-3">
          <Warning size={22} weight="fill" className="text-primary shrink-0 mt-0.5" />
          <div className="text-white/80 text-sm leading-relaxed">
            <strong className="text-primary">منطق صارم:</strong> الشركة المرتبطة بقالب تستخدم أسعار
            هذا القالب <strong>حصرياً</strong>. إن لم توجد قاعدة سعر لمسار ما داخل القالب،
            <strong> لن يتم الرجوع للأسعار العامة</strong> — سيظهر السعر كغير محدد. الشركات غير المرتبطة
            بأي قالب تستخدم الأسعار العامة كالمعتاد.
          </div>
        </div>

        {/* General rules card */}
        <div className="mb-5">
          <Link
            to="/admin/route-pricing"
            data-testid="card-general-rules"
            className="block rounded-2xl border-2 border-[#3B82F6]/40 bg-gradient-to-br from-[#0B1F3D] to-[#0F2A54] p-5 hover:border-[#3B82F6]/70 transition-all group"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#3B82F6]/15 border border-[#3B82F6]/40 flex items-center justify-center">
                  <TagIcon size={22} weight="duotone" className="text-[#3B82F6]" />
                </div>
                <div>
                  <div className="text-white font-bold text-lg">الأسعار العامة</div>
                  <div className="text-white/50 text-xs mt-0.5">
                    قواعد التسعير الافتراضية للشركات غير المرتبطة بأي قالب
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#3B82F6] num">
                    {fmtNum(generalCount)}
                  </div>
                  <div className="text-[10px] text-white/50">قاعدة</div>
                </div>
                <ArrowRight
                  size={20}
                  className="text-[#3B82F6] rotate-180 group-hover:-translate-x-1 transition-transform"
                />
              </div>
            </div>
          </Link>
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <MagnifyingGlass
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-primary/60"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ابحث عن قالب…"
              className="mz-input pr-9"
              data-testid="templates-search"
            />
          </div>
        </div>

        {/* Templates grid */}
        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : filtered.length === 0 ? (
          <div
            className="text-center py-16 text-white/60 bg-card border border-primary/15 rounded-2xl"
            data-testid="templates-empty"
          >
            <FolderPlus
              size={48}
              weight="duotone"
              className="text-primary/40 mx-auto mb-3"
            />
            <div className="mb-2">لا توجد قوالب أسعار بعد.</div>
            <div className="text-xs text-white/40">
              أنشئ قالباً جديداً وربط شركات به لتستخدم أسعاره حصرياً.
            </div>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="templates-grid"
          >
            {filtered.map((t) => {
              const cmp = t.companies || [];
              return (
                <div
                  key={t.id}
                  data-testid={`template-card-${t.id}`}
                  className="fadeup rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-[#0F2148] to-[#172956] p-5 hover:border-primary/70 hover:shadow-[0_0_0_1px_var(--primary)] transition-all flex flex-col"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center shrink-0">
                        <Folder size={22} weight="duotone" className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-white font-bold text-base truncate"
                          data-testid={`template-name-${t.id}`}
                        >
                          {t.name}
                        </div>
                        {t.description && (
                          <div className="text-white/50 text-[11px] mt-0.5 line-clamp-2">
                            {t.description}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => {
                          setEditing(t);
                          setModalOpen(true);
                        }}
                        data-testid={`edit-template-${t.id}`}
                        className="p-2 rounded-lg border border-primary/30 text-primary hover:bg-primary/10"
                        title="تعديل"
                      >
                        <Pencil size={13} weight="bold" />
                      </button>
                      <button
                        onClick={() => remove(t)}
                        data-testid={`delete-template-${t.id}`}
                        className="p-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10"
                        title="حذف"
                      >
                        <Trash size={13} weight="bold" />
                      </button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="rounded-lg bg-[#0B1A3A] border border-primary/20 p-2.5 text-center">
                      <div className="text-2xl font-bold text-primary num">
                        {fmtNum(t.rules_count || 0)}
                      </div>
                      <div className="text-[10px] text-white/50">قاعدة سعر</div>
                    </div>
                    <div className="rounded-lg bg-[#0B1A3A] border border-primary/20 p-2.5 text-center">
                      <div className="text-2xl font-bold text-[#4ADE80] num">
                        {fmtNum(cmp.length)}
                      </div>
                      <div className="text-[10px] text-white/50">شركة مرتبطة</div>
                    </div>
                  </div>

                  {/* Company chips */}
                  {cmp.length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] text-white/40 mb-1.5 flex items-center gap-1">
                        <Buildings size={11} weight="duotone" /> الشركات:
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {cmp.slice(0, 5).map((c) => (
                          <span
                            key={c.id}
                            className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 border border-primary/30 text-primary/90 max-w-[140px] truncate"
                            title={c.name}
                          >
                            {c.name}
                          </span>
                        ))}
                        {cmp.length > 5 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60 num">
                            +{fmtNum(cmp.length - 5)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Manage rules link */}
                  <Link
                    to={`/admin/route-pricing?template_id=${encodeURIComponent(
                      t.id,
                    )}&template_name=${encodeURIComponent(t.name)}`}
                    data-testid={`manage-rules-${t.id}`}
                    className="mt-auto inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-primary/15 border border-primary/40 text-primary hover:bg-primary/25 text-sm font-bold group"
                  >
                    <TagIcon size={14} weight="fill" /> إدارة قواعد الأسعار
                    <ArrowRight
                      size={14}
                      weight="bold"
                      className="rotate-180 group-hover:-translate-x-1 transition-transform"
                    />
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <TemplateModal
        open={modalOpen}
        initial={editing}
        allCompanies={enrichedCompanies}
        onClose={() => setModalOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
